import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import { ToggleButton, ToggleButtonGroup } from "../ui";
import { useOrganization } from "../hooks/useOrganization";
import { isRecordPath } from "../utils/organizationScope";

/* A recessed track in the organization's dark secondary with the selected
   button lit in its `highlight` - gold on Windward's green, white on Kimball's
   black. The outlined grey pair it replaces blended into the bar; the owner
   chose this from the prototypes of 2026-10-06. The track is dark enough to
   read on the white mobile drawer as well as on the bar. */
const segments = {
  p: "3px",
  gap: "3px",
  borderRadius: "11px",
  bgcolor: "secondary.main",
  boxShadow:
    "inset 0 1px 3px rgba(0,0,0,.55), inset 0 0 0 1px rgba(255,255,255,.12)",
  "& .MuiToggleButtonGroup-grouped": {
    border: 0,
    borderRadius: "8px !important",
    m: 0,
  },
  "& .MuiToggleButton-root": {
    px: 1.5,
    py: 0.6,
    color: "rgba(255,255,255,.75)",
    fontSize: "0.9rem",
    fontWeight: 650,
    textTransform: "none",
    whiteSpace: "nowrap",
    transition: "background-color .25s, color .25s",
    "&:hover": { color: "common.white", bgcolor: "rgba(255,255,255,.08)" },
  },
  "& .MuiToggleButton-root.Mui-selected": {
    color: "highlight.contrastText",
    bgcolor: "highlight.main",
    fontWeight: 750,
    boxShadow: "0 1px 2px rgba(0,0,0,.35), 0 3px 8px rgba(0,0,0,.28)",
    "&:hover": { bgcolor: "highlight.main" },
  },
};

/**
 * The organization switch in the app bar: one button per organization this
 * user may use, the selected one lit. Nothing at all with one, which is the app
 * as it was (docs/changes/organization-scoping/SPEC.md 3d) and what a user an
 * Admin restricted to one organization sees (PLAN.md 3.6b).
 *
 * Switching clears the secure-storage form draft the way navigation already
 * does, then goes home from a record screen - the record belonged to the
 * other organization - and stays put on a list, which App.jsx re-mounts by
 * keying the route table on the organization (SPEC Q7).
 */
export default function OrganizationSwitch({ vertical = false, sx, tour }) {
  const { id, available: organizations, select } = useOrganization();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  if (organizations.length < 2) return null;

  const onChange = (_event, next) => {
    if (!next || next === id) return;
    secureLocalStorage.removeItem("form");
    select(next);
    if (isRecordPath(pathname)) navigate("/");
  };

  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={id}
      onChange={onChange}
      orientation={vertical ? "vertical" : "horizontal"}
      aria-label="Organization"
      data-tour={tour}
      sx={[segments, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {organizations.map((organization) => (
        <ToggleButton
          key={organization.id}
          value={organization.id}
          aria-label={organization.name}
        >
          {organization.shortName}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
